// Shape tools (M rectangle, L ellipse, polygon, star, \ line): drag to draw a live shape with the current
// appearance. Rectangle / ellipse: Shift for a square / circle, Alt to draw from the centre. Polygon / star:
// drawn from the centre, Shift keeps them upright. Line: Shift for 0/45/90°. A click without a drag makes a
// 100 pt shape there. The new object is selected and the Selection tool comes back, like Illustrator.
import type { Tool, ToolHost, ToolPointer } from './tool';
import type { Mat2D, Node, Shape } from '../model/types';
import { useDesign } from '../state';
import { useDesignUi } from '../ui/designUi';
import { addNode } from '../commands/commands';
import { newId } from '../model/ids';
import { shapeToSubpaths, tracePath } from '../geometry/bezier';
import { toScreen } from '../render/viewMath';
import { drawSnapGuides, snapContext, type SnapCtx } from './snapContext';
import type { Guide } from '../geometry/snapping';

export type ShapeKind = 'rect' | 'ellipse' | 'polygon' | 'star' | 'line';

/** The live shape (in local coordinates, origin at `at`) for a drag from (x0, y0) to (x1, y1). */
export function shapeFromDrag(
  kind: ShapeKind,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  shift: boolean,
  alt: boolean,
): { shape: Shape; at: [number, number]; rot: number } {
  let dx = x1 - x0,
    dy = y1 - y0;
  if (kind === 'line') {
    if (shift) {
      const a = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
      const len = Math.hypot(dx, dy);
      dx = len * Math.cos(a);
      dy = len * Math.sin(a);
    }
    return { shape: { type: 'line', x1: 0, y1: 0, x2: dx, y2: dy }, at: [x0, y0], rot: 0 };
  }
  if (kind === 'polygon' || kind === 'star') {
    const r = Math.hypot(dx, dy);
    const rot = shift ? 0 : Math.atan2(dy, dx) + Math.PI / 2;
    const shape: Shape =
      kind === 'polygon'
        ? { type: 'polygon', cx: 0, cy: 0, r, sides: 6 }
        : { type: 'star', cx: 0, cy: 0, r1: r, r2: r * 0.45, points: 5 };
    return { shape, at: [x0, y0], rot };
  }
  if (shift) {
    const s = Math.max(Math.abs(dx), Math.abs(dy));
    dx = Math.sign(dx || 1) * s;
    dy = Math.sign(dy || 1) * s;
  }
  let x = x0,
    y = y0,
    w = dx,
    h = dy;
  if (alt) {
    x = x0 - dx;
    y = y0 - dy;
    w = 2 * dx;
    h = 2 * dy;
  }
  const ox = Math.min(x, x + w),
    oy = Math.min(y, y + h);
  w = Math.abs(w);
  h = Math.abs(h);
  const shape: Shape =
    kind === 'rect'
      ? { type: 'rect', x: 0, y: 0, w, h, radii: [0, 0, 0, 0] }
      : { type: 'ellipse', cx: w / 2, cy: h / 2, rx: w / 2, ry: h / 2 };
  return { shape, at: [ox, oy], rot: 0 };
}

export class ShapeTool implements Tool {
  private start: [number, number] | null = null;
  private end: [number, number] = [0, 0];
  private mods = { shift: false, alt: false };
  private snap: SnapCtx | null = null;
  private guides: Guide[] = [];
  constructor(readonly kind: ShapeKind) {}

  down(p: ToolPointer) {
    this.snap = snapContext();
    const s = this.snap.point(p.wx, p.wy);
    this.start = [s.x, s.y];
    this.end = [s.x, s.y];
  }
  /** The pointer, snapped (not while Shift constrains the shape). */
  private at(p: ToolPointer): [number, number] {
    if (!this.snap || p.shift) {
      this.guides = [];
      return [p.wx, p.wy];
    }
    const s = this.snap.point(p.wx, p.wy);
    this.guides = s.guides;
    return [s.x, s.y];
  }
  move(p: ToolPointer, h: ToolHost, dragging: boolean) {
    h.setCursor('crosshair');
    if (!dragging || !this.start) return;
    this.end = this.at(p);
    this.mods = { shift: p.shift, alt: p.alt };
    h.redrawOverlay();
  }
  up(p: ToolPointer, h: ToolHost) {
    if (!this.start) return;
    const [x0, y0] = this.start;
    this.start = null;
    let [x1, y1] = this.at(p);
    this.guides = [];
    // A click (less than 3 px of movement): a 100 pt shape.
    if (Math.hypot(x1 - x0, y1 - y0) < 3 * p.px) {
      x1 = x0 + 100;
      y1 = this.kind === 'line' ? y0 : y0 + 100;
    }
    const node = makeShapeNode(this.kind, x0, y0, x1, y1, p.shift, p.alt);
    useDesign.getState().run(addNode(node, activeLayerId()));
    useDesignUi.getState().set({ selection: [node.id], tool: 'select' });
    h.redrawOverlay();
  }
  overlay(g: CanvasRenderingContext2D, v: ReturnType<ToolHost['view']>) {
    drawSnapGuides(g, v, this.guides);
    if (!this.start) return;
    const { shape, at, rot } = shapeFromDrag(
      this.kind,
      this.start[0],
      this.start[1],
      this.end[0],
      this.end[1],
      this.mods.shift,
      this.mods.alt,
    );
    const [sx, sy] = toScreen(v, at[0], at[1]);
    g.save();
    g.translate(sx, sy);
    g.scale(v.zoom, v.zoom);
    g.rotate(rot);
    const p = new Path2D();
    tracePath(p, shapeToSubpaths(shape));
    g.lineWidth = 1 / v.zoom;
    g.strokeStyle = '#4f8cff';
    g.stroke(p);
    g.restore();
  }
}

export function makeShapeNode(
  kind: ShapeKind,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  shift: boolean,
  alt: boolean,
): Node {
  const r = shapeFromDrag(kind, x0, y0, x1, y1, shift, alt);
  const c = Math.cos(r.rot),
    s = Math.sin(r.rot);
  const transform: Mat2D = [c, s, -s, c, r.at[0], r.at[1]];
  const appearance = structuredClone(useDesignUi.getState().current);
  // A line has no fill; give it the current stroke (or a 1 pt black one).
  if (kind === 'line') {
    appearance.fills = [];
    if (!appearance.strokes.length)
      appearance.strokes = [
        {
          paint: { kind: 'solid', color: { rgb: [0, 0, 0], alpha: 1 } },
          width: 1,
          cap: 'butt',
          join: 'miter',
          miter: 10,
          dash: [],
          dashOffset: 0,
          align: 'center',
          opacity: 1,
          blend: 'normal',
        },
      ];
  }
  return { id: newId(), kind: 'shape', shape: r.shape, visible: true, locked: false, transform, appearance };
}

/** New objects go into the layer of the selection, else the frontmost unlocked visible layer. */
export function activeLayerId(): string | undefined {
  const doc = useDesign.getState().doc;
  if (!doc) return undefined;
  const sel = new Set(useDesignUi.getState().selection);
  const withSel = doc.layers.find((l) => l.children.some((c) => sel.has(c.id)));
  if (withSel && !withSel.locked) return withSel.id;
  return (
    [...doc.layers].reverse().find((l) => !l.locked && l.visible)?.id ?? doc.layers[doc.layers.length - 1]?.id
  );
}
